import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Select,
  Button,
  Space,
  Modal,
  Typography,
  Divider,
  message,
  Tooltip,
  Dropdown,
} from "antd";
import { EyeOutlined, DownloadOutlined, DownOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import dayjs from "dayjs";
import Pagination from "../components/Pagination";
import {
  fetchWeeklySubmissions,
  exportWeeklySubmissionsAsCSV,
  exportWeeklySubmissionsAsExcel,
} from "../services/submissionService";

const { Text, Paragraph } = Typography;
const { Option } = Select;

interface WeeklySubmission {
  _id: string;
  participantId: {
    userId: {
      _id: string;
      email: string;
      fullName?: string;
      phoneNumber?: string;
    };
  };
  hackathonId: {
    title: string;
  };
  weekNumber: number;
  submissionDate: string;
  lastWeekWork: string;
  currentWeekPlans: string;
  challengesSupport?: string;
  status: "submitted" | "reviewed" | "needs_followup";
}

const WeeklySubmission: React.FC = () => {
  const [submissions, setSubmissions] = useState<WeeklySubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<WeeklySubmission | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [filters, setFilters] = useState({
    weekNumber: "",
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const columns: ColumnsType<WeeklySubmission> = [
    {
      title: "Participant",
      key: "participant",
      width: 200,
      render: (_, record) => (
        <span>
          {record.participantId?.userId?.fullName ||
            record.participantId?.userId?.email ||
            "N/A"}
        </span>
      ),
    },
    {
      title: "Hackathon",
      dataIndex: ["hackathonId", "title"],
      key: "hackathon",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Week",
      dataIndex: "weekNumber",
      key: "weekNumber",
      width: 80,
      sorter: (a, b) => a.weekNumber - b.weekNumber,
    },
    {
      title: "Submitted",
      dataIndex: "submissionDate",
      key: "submissionDate",
      width: 120,
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
      sorter: (a, b) =>
        dayjs(a.submissionDate).unix() - dayjs(b.submissionDate).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record: WeeklySubmission) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewSubmission(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const response = await fetchWeeklySubmissions({
        weekNumber: filters.weekNumber || undefined,
        page: pagination.current,
        limit: pagination.pageSize,
      });

      if (response.success) {
        setSubmissions(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.totalItems,
          totalPages: response.pagination.totalPages,
        }));
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to fetch weekly submissions"
      );
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, [filters, pagination.current, pagination.pageSize]);

  const handleViewSubmission = (submission: WeeklySubmission) => {
    setSelectedSubmission(submission);
    setModalVisible(true);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
    }));
  };

  const handleExport = async (format: "csv" | "excel") => {
    try {
      message.loading(`Exporting as ${format.toUpperCase()}...`);
      if (format === "csv") {
        await exportWeeklySubmissionsAsCSV(filters);
      } else {
        await exportWeeklySubmissionsAsExcel(filters);
      }
      message.success(`Successfully exported as ${format.toUpperCase()}`);
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          `Failed to export as ${format.toUpperCase()}`
      );
    }
  };

  const exportMenuItems: MenuProps["items"] = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => handleExport("csv"),
    },
    {
      key: "excel",
      label: "Export as Excel",
      onClick: () => handleExport("excel"),
    },
  ];

  const getWeekOptions = () => {
    // Generate week options 1-4
    return [1, 2, 3, 4].map((week) => ({
      label: `Week ${week}`,
      value: week.toString(),
    }));
  };

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[16, 16]}>
        {/* Filters */}
        <Col span={24}>
          <Row gutter={16} align="middle" justify="space-between">
            <Col>
              <Dropdown
                menu={{ items: exportMenuItems }}
                placement="bottomRight"
              >
                <Button icon={<DownloadOutlined />}>
                  Export <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Select Week"
                style={{ width: "100%" }}
                allowClear
                onChange={(value) => handleFilterChange("weekNumber", value)}
              >
                {getWeekOptions().map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Col>

        {/* Submissions Table */}
        <Col span={24}>
          <Card title="Weekly Submissions">
            <Table
              columns={columns}
              dataSource={submissions}
              loading={loading}
              rowKey="_id"
              scroll={{ x: 1000 }}
              pagination={false}
            />
          </Card>
        </Col>

        {/* Custom Pagination */}
        <Col span={24}>
          <Pagination
            currentPage={pagination.current}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </Col>
      </Row>

      {/* Submission Details Modal */}
      <Modal
        title="Weekly Submission Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedSubmission && (
          <div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <Text strong>Participant:</Text>
                <br />
                <Text>
                  {selectedSubmission.participantId?.userId?.fullName ||
                    selectedSubmission.participantId?.userId?.email ||
                    "N/A"}
                </Text>
              </div>
              <div className="col-span-1">
                <Text strong>Email:</Text>
                <br />
                <Text>
                  {selectedSubmission.participantId?.userId?.email || "N/A"}
                </Text>
              </div>
              <div className="col-span-1">
                <Text strong>Phone Number:</Text>
                <br />
                <Text>
                  {selectedSubmission.participantId?.userId?.phoneNumber ||
                    "N/A"}
                </Text>
              </div>
            </div>

           

            <Divider />

            <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
                <Text strong>Hackathon:</Text>
                <br />
                <Text>{selectedSubmission.hackathonId.title}</Text>
              </div>
              <div className="col-span-1">
                <Text strong>Week Number:</Text>
                <br />
                <Text>Week {selectedSubmission.weekNumber}</Text>
              </div>
              <div className="col-span-1">
                <Text strong>Submitted:</Text>
                <br />
                <Text>
                  {dayjs(selectedSubmission.submissionDate).format(
                    "MMMM DD, YYYY [at] h:mm A"
                  )}
                </Text>
              </div>
            </div>

            <Divider />

            <div>
              <Text strong>What did you work on last week?</Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  background: "#f5f5f5",
                  padding: 12,
                  borderRadius: 4,
                }}
              >
                {selectedSubmission.lastWeekWork}
              </Paragraph>
            </div>

            <div>
              <Text strong>What are your plans for this week?</Text>
              <Paragraph
                style={{
                  marginTop: 8,
                  background: "#f5f5f5",
                  padding: 12,
                  borderRadius: 4,
                }}
              >
                {selectedSubmission.currentWeekPlans}
              </Paragraph>
            </div>

            {selectedSubmission.challengesSupport && (
              <div>
                <Text strong>Any challenges or support needed?</Text>
                <Paragraph
                  style={{
                    marginTop: 8,
                    background: "#f5f5f5",
                    padding: 12,
                    borderRadius: 4,
                  }}
                >
                  {selectedSubmission.challengesSupport}
                </Paragraph>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WeeklySubmission;
